import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from "react";
import { useSelector } from "react-redux";

// eslint-disable-next-line import/no-named-as-default
import produce from "immer";
import { RootState } from "store/rootReducer";
import { selectSelectedFacilities, selectSelectedWells, useMapStore } from "stores/map";

import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import { EntityKind } from "../../../../../models/entityKind";
import { useFilterUndo } from "../hooks/useFilterUndo";

export enum FilterKey {
  HighlightWells = "highlight-wells",
  FacilityAndWells = "facility-and-wells",
  ContributingWellsFromAllFacilities = "contributing-wells-from-all-facilities",
  SalesFacilities = "sales-facilities",
  WellNetwork = "well-network"
}

export enum FilterButtonActionType {
  Update = "update",
  SyncReduxState = "syncReduxState"
}

const FACILITY_BUTTONS = [
  FilterKey.HighlightWells,
  FilterKey.FacilityAndWells,
  FilterKey.ContributingWellsFromAllFacilities
];

const WELL_BUTTONS = [FilterKey.WellNetwork, FilterKey.SalesFacilities];

// External filter monitoring strategy types
export type ExternalFilterMonitoringStrategy = (
  isActive: boolean,
  userActionInProgress: boolean
) => boolean; // returns shouldRemove

// Predefined monitoring strategies factory
export const createMonitoringStrategies = (
  getCurrentReduxState: () => {
    isMidstreamNavigatorFilter: boolean;
    isMidstreamNavigatorFacilityFilter: boolean;
  }
) => ({
  // Standard facility filter monitoring
  facilityFilter: (isActive: boolean, userActionInProgress: boolean): boolean => {
    if (!isActive || userActionInProgress) return false;
    const currentReduxState = getCurrentReduxState();
    return !currentReduxState.isMidstreamNavigatorFacilityFilter;
  },

  // Standard well filter monitoring
  wellFilter: (isActive: boolean, userActionInProgress: boolean): boolean => {
    if (!isActive || userActionInProgress) return false;
    const currentReduxState = getCurrentReduxState();
    return !currentReduxState.isMidstreamNavigatorFilter;
  },

  // Contributing wells - expects both flags to be true, removes if either is false
  contributingWells: (isActive: boolean, userActionInProgress: boolean): boolean => {
    if (!isActive || userActionInProgress) return false;
    const currentReduxState = getCurrentReduxState();

    return (
      !currentReduxState.isMidstreamNavigatorFilter ||
      !currentReduxState.isMidstreamNavigatorFacilityFilter
    );
  },

  // Sales facilities - skips monitoring during well selection changes
  salesFacilities: (isActive: boolean, userActionInProgress: boolean): boolean => {
    if (!isActive || userActionInProgress) return false;

    const currentReduxState = getCurrentReduxState();

    return !currentReduxState.isMidstreamNavigatorFacilityFilter;
  },

  // No monitoring (for highlight-only buttons)
  none: (): boolean => false
});

// State interface following MapContext pattern
export interface FilterButtonContextState {
  userActionInProgress: boolean;
  selectedWells: Maybe<Record<string, unknown>>;
  selectedFacilities: Maybe<Record<string, unknown>>;
  isMidstreamNavigatorFilter: boolean;
  isMidstreamNavigatorFacilityFilter: boolean;
  facilityFilterCount: number;
  wellFilterCount: number;
}

// State update types
export interface LastSelectedEntityTypeState {
  lastSelectedEntityType: Maybe<EntityKind>;
}

export interface UserActionInProgressState {
  userActionInProgress: boolean;
}

export interface SelectedWellsState {
  selectedWells: Maybe<Record<string, unknown>>;
}

export interface SelectedFacilitiesState {
  selectedFacilities: Maybe<Record<string, unknown>>;
}

export interface ExternalFilterState {
  isMidstreamNavigatorFilter: boolean;
  isMidstreamNavigatorFacilityFilter: boolean;
}

// Initial state
export const initialFilterButtonContextState: FilterButtonContextState = {
  userActionInProgress: false,
  selectedWells: null,
  selectedFacilities: null,
  isMidstreamNavigatorFilter: false,
  isMidstreamNavigatorFacilityFilter: false,
  facilityFilterCount: 0,
  wellFilterCount: 0
};

// Action interface
export interface Action<T, Type> {
  type?: Type;
  payload: T;
}

// Update types
export type FilterButtonUpdates =
  | LastSelectedEntityTypeState
  | UserActionInProgressState
  | SelectedWellsState
  | SelectedFacilitiesState
  | ExternalFilterState
  | FilterButtonContextState;

// Consolidated Redux state sync action
export interface SyncReduxStateAction {
  type: FilterButtonActionType.SyncReduxState;
  payload: {
    selectedWells: Maybe<Record<string, unknown>>;
    selectedFacilities: Maybe<Record<string, unknown>>;
    isMidstreamNavigatorFilter: boolean;
    isMidstreamNavigatorFacilityFilter: boolean;
    facilityFilterCount: number;
    wellFilterCount: number;
  };
}

// Enhanced provider with computed functions
interface FilterButtonContextWithFunctions extends FilterButtonContextState {
  // Helper functions
  isInActiveFilter: (filterKey: FilterKey) => boolean;
  getIsDisabled: (filterKey: FilterKey, isActive: boolean) => boolean;
  clearOtherFacilityFilters: (currentFilterKey: FilterKey) => void;
  withUserAction: (callback: () => void) => void;
  useExternalFilterMonitoring: (
    filterKey: FilterKey,
    isActive: boolean,
    strategy?: ExternalFilterMonitoringStrategy,
    undoFilters?: (isLassoSelection: boolean) => void
  ) => void;
  MonitoringStrategies: ReturnType<typeof createMonitoringStrategies>;
}

// Single unified context
export const FilterButtonContext = createContext<FilterButtonContextWithFunctions>({
  ...initialFilterButtonContextState,
  isInActiveFilter: () => false,
  getIsDisabled: () => true,
  clearOtherFacilityFilters: () => {
    // Default implementation - will be overridden by provider
  },
  withUserAction: () => {
    // Default implementation - will be overridden by provider
  },
  useExternalFilterMonitoring: () => {
    // Default implementation - will be overridden by provider
  },
  MonitoringStrategies: createMonitoringStrategies(() => ({
    isMidstreamNavigatorFilter: false,
    isMidstreamNavigatorFacilityFilter: false
  }))
});

export const FilterButtonDispatchContext =
  createContext<
    React.Dispatch<
      Action<FilterButtonUpdates, FilterButtonActionType.Update> | SyncReduxStateAction
    >
  >(undefined);

// Reducer function
function filterButtonReducer(
  state: FilterButtonContextState,
  action:
    | Action<FilterButtonUpdates, FilterButtonActionType.Update>
    | SyncReduxStateAction
) {
  return produce(state, (draft) => {
    if (action.type === FilterButtonActionType.SyncReduxState) {
      // Consolidated Redux state sync
      const {
        selectedWells,
        selectedFacilities,
        isMidstreamNavigatorFilter,
        isMidstreamNavigatorFacilityFilter,
        wellFilterCount,
        facilityFilterCount
      } = action.payload;

      // Update all Redux-derived state at once
      draft.selectedWells = selectedWells;
      draft.selectedFacilities = selectedFacilities;
      draft.isMidstreamNavigatorFilter = isMidstreamNavigatorFilter;
      draft.isMidstreamNavigatorFacilityFilter = isMidstreamNavigatorFacilityFilter;
      draft.wellFilterCount = wellFilterCount;
      draft.facilityFilterCount = facilityFilterCount;
    } else {
      // Regular update action
      Object.assign(draft, action.payload);
    }
  });
}

// Provider interface
export interface FilterButtonProviderModel {
  children: ReactNode;
  state?: FilterButtonContextState;
}

// Provider component
export function FilterButtonProvider({
  children,
  state: overrideState
}: FilterButtonProviderModel) {
  const [state, dispatch] = useReducer(
    filterButtonReducer,
    overrideState ?? initialFilterButtonContextState
  );

  const [midstreamState, midstreamDispatch] = useMidstreamNavigatorContext();
  const { undoFilters } = useFilterUndo();
  const userActionInProgressRef = useRef(false);
  const debounceTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const lastSelectedEntityTypeRef = useRef<Maybe<EntityKind>>(null);
  const previousSelectedWellsRef = useRef<Maybe<Record<string, unknown>>>(null);

  // Map store selectors (Zustand)
  const selectedWells = useMapStore(selectSelectedWells);
  const selectedFacilities = useMapStore(selectSelectedFacilities);
  const isMidstreamNavigatorFilter = useSelector(
    (state: RootState) => state.filter.isMidstreamNavigatorFilter
  );
  const isMidstreamNavigatorFacilityFilter = useSelector(
    (state: RootState) => state.facilityFilter.isMidstreamNavigatorFilter
  );
  const facilityFilterCount = useSelector(
    (state: RootState) => state.facilityFilter.filterCounts.Active
  );
  const wellFilterCount = useSelector(
    (state: RootState) => state.filter.filterCounts.Active
  );

  // Consolidated Redux state sync - single useEffect instead of multiple
  useEffect(() => {
    dispatch({
      type: FilterButtonActionType.SyncReduxState,
      payload: {
        selectedWells,
        selectedFacilities,
        isMidstreamNavigatorFilter,
        isMidstreamNavigatorFacilityFilter,
        facilityFilterCount,
        wellFilterCount
      }
    });
  }, [
    selectedWells,
    selectedFacilities,
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter,
    facilityFilterCount,
    wellFilterCount
  ]);

  // Helper function to detect well selection changes
  const hasWellSelectionChanged = useCallback(() => {
    const currentWells = selectedWells;
    const previousWells = previousSelectedWellsRef.current ?? selectedWells;

    // Check if the selection has actually changed
    const currentWellIds = currentWells ? Object.keys(currentWells).sort() : [];
    const previousWellIds = previousWells ? Object.keys(previousWells).sort() : [];

    const hasChanged =
      currentWellIds.length !== previousWellIds.length ||
      !currentWellIds.every((id, index) => id === previousWellIds[index]);

    // Update the ref for next comparison
    previousSelectedWellsRef.current = currentWells;

    return hasChanged;
  }, [selectedWells]);

  // Create monitoring strategies with well selection change detection and current Redux state access
  const MonitoringStrategies = useMemo(() => {
    const getCurrentReduxState = () => ({
      isMidstreamNavigatorFilter,
      isMidstreamNavigatorFacilityFilter
    });
    return createMonitoringStrategies(getCurrentReduxState);
  }, [
    hasWellSelectionChanged,
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter
  ]);

  // Track last selected entity type in ref (separate from state to avoid unnecessary re-renders)
  // Split into separate effects to track wells and facilities independently
  useEffect(() => {
    const hasSelectedWells = selectedWells && Object.keys(selectedWells).length > 0;
    if (hasSelectedWells) {
      lastSelectedEntityTypeRef.current = EntityKind.Well;
    }
  }, [selectedWells]);

  useEffect(() => {
    const hasSelectedFacilities =
      selectedFacilities && Object.keys(selectedFacilities).length > 0;
    if (hasSelectedFacilities) {
      lastSelectedEntityTypeRef.current = EntityKind.Facility;
    }
  }, [selectedFacilities]);

  // Clear entity type when both are empty
  useEffect(() => {
    const hasSelectedWells = selectedWells && Object.keys(selectedWells).length > 0;
    const hasSelectedFacilities =
      selectedFacilities && Object.keys(selectedFacilities).length > 0;

    if (!hasSelectedWells && !hasSelectedFacilities) {
      lastSelectedEntityTypeRef.current = null;
    }
  }, [selectedWells, selectedFacilities]);

  // Helper function to check if a filter is active (memoized for performance)
  const isInActiveFilter = useCallback(
    (filterKeyToCheck: FilterKey): boolean =>
      midstreamState.activeFilters?.some((key) => key === filterKeyToCheck) ?? false,
    [midstreamState.activeFilters]
  );

  // Shared function to clear other facility filters (mutual exclusion) - memoized
  const clearOtherFacilityFilters = useCallback(
    (currentFilterKey: FilterKey): void => {
      const existingFacilityFilters = FACILITY_BUTTONS.filter(
        (button) =>
          button !== currentFilterKey && midstreamState.activeFilters?.includes(button)
      );

      existingFacilityFilters.forEach((existingFilter) => {
        midstreamDispatch(midstreamNavigatorActions.removeFilter(existingFilter));
      });

      // Clear filters before activating new one
      if (existingFacilityFilters.length > 0) {
        undoFilters();
      }
    },
    [midstreamState.activeFilters, midstreamDispatch, undoFilters]
  );

  // Shared function to wrap actions with user action flag management (debounced)
  const withUserAction = useCallback(
    (callback: () => void): void => {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set user action in progress immediately
      if (!userActionInProgressRef.current) {
        userActionInProgressRef.current = true;
        dispatch({
          type: FilterButtonActionType.Update,
          payload: { userActionInProgress: true }
        });
      }

      // Execute the callback
      callback();

      // Debounce the reset - only reset after user stops performing actions
      debounceTimeoutRef.current = setTimeout(() => {
        userActionInProgressRef.current = false;
        dispatch({
          type: FilterButtonActionType.Update,
          payload: { userActionInProgress: false }
        });
        debounceTimeoutRef.current = null;
      }, 500); // Increased from 150ms to 500ms
    },
    [dispatch]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Shared external filter monitoring hook with customizable strategy
  const useExternalFilterMonitoring = (
    filterKey: FilterKey,
    isActive: boolean,
    strategy?: ExternalFilterMonitoringStrategy
  ): void => {
    useEffect(() => {
      // Add delay for contributing wells to allow flag updates to process
      const delay = filterKey === FilterKey.ContributingWellsFromAllFacilities ? 100 : 0;

      const timeoutId = setTimeout(() => {
        // Use custom strategy if provided, otherwise fall back to default logic
        const shouldRemove = strategy
          ? strategy(isActive, userActionInProgressRef.current)
          : getDefaultMonitoringDecision(
              filterKey,
              isActive,
              state,
              userActionInProgressRef.current
            );

        if (shouldRemove) {
          midstreamDispatch(midstreamNavigatorActions.removeFilter(filterKey));
        }
      }, delay);

      return () => clearTimeout(timeoutId);
    }, [
      state.isMidstreamNavigatorFilter,
      state.isMidstreamNavigatorFacilityFilter,
      isActive,
      filterKey,
      strategy
    ]);
  };

  // Default monitoring logic (fallback for buttons without custom strategies)
  const getDefaultMonitoringDecision = (
    filterKey: FilterKey,
    isActive: boolean,
    state: FilterButtonContextState,
    userActionInProgress: boolean
  ): boolean => {
    // Don't auto-remove if user is actively interacting with toolbar
    if (userActionInProgress || !isActive) {
      return false;
    }

    // Determine if this is a facility or well filter button
    const isFacilityFilterButton = FACILITY_BUTTONS.includes(filterKey);
    const isWellFilterButton = WELL_BUTTONS.includes(filterKey);

    // Special handling for buttons that filter both wells and facilities (like contributing wells)
    const isContributingWellsButton =
      filterKey === FilterKey.ContributingWellsFromAllFacilities;

    // Check the appropriate filter state based on button type
    if (isContributingWellsButton) {
      // For contributing wells, we expect both flags to be true
      // Remove if either flag is false (using OR logic to match contributingWells strategy)
      return !isMidstreamNavigatorFilter || !isMidstreamNavigatorFacilityFilter;
    } else if (isFacilityFilterButton) {
      return !isMidstreamNavigatorFacilityFilter;
    } else if (isWellFilterButton) {
      return !isMidstreamNavigatorFilter;
    }

    return false;
  };

  // Comprehensive isDisabled logic from original FilterButton - memoized for performance
  const getIsDisabled = useCallback(
    (filterKey: FilterKey, isActive: boolean): boolean => {
      const hasSelectedWells =
        state.selectedWells && Object.keys(state.selectedWells).length > 0;
      const hasSelectedFacilities =
        state.selectedFacilities && Object.keys(state.selectedFacilities).length > 0;

      const isFacilityFilterButton = (FACILITY_BUTTONS as readonly FilterKey[]).includes(
        filterKey
      );
      const isWellFilterButton = (WELL_BUTTONS as readonly FilterKey[]).includes(
        filterKey
      );

      const hasNoSelectedEntity = !hasSelectedWells && !hasSelectedFacilities;

      const isWrongEntityType = (() => {
        if (
          hasSelectedWells &&
          isFacilityFilterButton &&
          lastSelectedEntityTypeRef.current === EntityKind.Well
        ) {
          return true;
        }
        if (
          hasSelectedFacilities &&
          isWellFilterButton &&
          lastSelectedEntityTypeRef.current === EntityKind.Facility
        ) {
          return true;
        }

        return false;
      })(); // Only disable when switching entity types, based on lastSelectedEntityType
      if (hasNoSelectedEntity || isWrongEntityType) {
        return true;
      }

      // For facility buttons, disable if no facilities selected
      if (isFacilityFilterButton && !hasSelectedFacilities) {
        return true;
      }

      // For well buttons, allow multiple to be active (less restrictive)
      if (isWellFilterButton) {
        return false; // Well buttons can be active simultaneously
      }

      // For facility buttons, allow if this button is active OR no other facility filters are active
      if (isFacilityFilterButton) {
        // Special case: HighlightWells can always be enabled alongside other facility filters
        if (filterKey === FilterKey.HighlightWells) {
          return false;
        }

        const otherActiveFacilityFilters = FACILITY_BUTTONS.filter(
          (button) =>
            button !== filterKey &&
            button !== FilterKey.HighlightWells &&
            isInActiveFilter(button)
        );
        return otherActiveFacilityFilters.length > 0 && !isActive;
      }

      return false;
    },
    [state.selectedWells, state.selectedFacilities, isInActiveFilter]
  );

  // Create enhanced context value with functions - memoized to prevent unnecessary re-renders
  const contextValue: FilterButtonContextWithFunctions = useMemo(
    () => ({
      ...state,
      isInActiveFilter,
      getIsDisabled,
      clearOtherFacilityFilters,
      withUserAction,
      useExternalFilterMonitoring,
      MonitoringStrategies
    }),
    [
      state,
      isInActiveFilter,
      getIsDisabled,
      clearOtherFacilityFilters,
      withUserAction,
      useExternalFilterMonitoring,
      MonitoringStrategies
    ]
  );

  return (
    <FilterButtonContext.Provider value={contextValue}>
      <FilterButtonDispatchContext.Provider value={dispatch}>
        {children}
      </FilterButtonDispatchContext.Provider>
    </FilterButtonContext.Provider>
  );
}

export { useFilterButtonContext } from "../hooks/useFilterButtonContext";
export { useFilterButtonDispatch } from "../hooks/useFilterButtonDispatch";

export function useFilterButtonState() {
  const context = useContext(FilterButtonContext);
  if (context === undefined) {
    throw new Error("useFilterButtonState must be used within a FilterButtonProvider");
  }
  return context;
}

// Export constants for use in components
export { FACILITY_BUTTONS, WELL_BUTTONS };
